import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AccessMatrix, LabGeofence, LabSettings, ProfileRange, UserAccessOverride } from '@/domain';
import { useActivityRepository, useAdminRepository } from '../container';
import { keys } from '../query-keys';
import { liveRefetchInterval } from '../live';
import type { ActivityFilter } from '../ports/ActivityRepository';
import type {
  CatalogItemEdit,
  NewCatalogItem,
  NewUser,
  UserEdit,
  AppIconUpload,
  PwaSettings,
} from '../ports/AdminRepository';

export function useActivity(filter: ActivityFilter) {
  const repo = useActivityRepository();
  return useQuery({
    queryKey: keys.activity(filter),
    queryFn: () => repo.list(filter),
    // The log grows on every mutation anywhere in the building, so it goes
    // stale quickly. Short, not zero: switching a filter twice should not cost
    // two round trips.
    staleTime: 10_000,
    placeholderData: (previous) => previous,
  });
}

export function useUsers() {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: keys.users,
    queryFn: () => repo.users(),
    staleTime: 30_000,
  });
}

/**
 * Every case a person worked, newest first.
 *
 * `keepPreviousData` so paging does not blank the table under the reader's
 * finger, which on a long record is the difference between reading it and
 * losing your place.
 */
export function useUserCases(userId: string | undefined, page: number, pageSize: number) {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: keys.userCases(userId ?? '', { page, pageSize }),
    queryFn: () => repo.userCases(userId as string, page, pageSize),
    enabled: userId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function useGeofence() {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: ['geofence'],
    queryFn: () => repo.geofence(),
    staleTime: 60_000,
  });
}

export function useSaveGeofence() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fence: LabGeofence) => repo.saveGeofence(fence),
    onSuccess: (saved) => {
      queryClient.setQueryData(['geofence'], saved);
      // The fence rides on the session, so a change has to reach it or the
      // next sign in would be checked against the old radius.
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** The routing mode. Section 2.3 of the system test handover. */
export function useLabSettings() {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => repo.settings(),
    staleTime: 60_000,
  });
}

export function useSaveLabSettings() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: LabSettings) => repo.saveSettings(settings),
    onSuccess: (saved) => {
      queryClient.setQueryData(['settings'], saved);
      // The mode rides on the session, and the job view reads it from there,
      // so the change has to reach it or Mark done would keep the old rule.
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** One person's exceptions to their role, for the card on their record. */
export function useUserAccess(userId: string | undefined, enabled: boolean) {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: keys.userAccess(userId ?? ''),
    queryFn: () => repo.userAccess(userId as string),
    enabled: enabled && userId !== undefined,
  });
}

/**
 * Saving a person's exceptions refreshes the team list, for the pill, and the log.
 * Not the session: this is somebody else's access, and it reaches them on their
 * own next sign in or refresh, which the card says.
 */
export function useSaveUserAccess() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      userId: string;
      rowVersion: string;
      access: UserAccessOverride & { note: string | null };
    }) => repo.saveUserAccess(input.userId, input.rowVersion, input.access),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.userAccess(saved.userId), saved);
      void queryClient.invalidateQueries({ queryKey: keys.users });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useAccessMatrix() {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: keys.access,
    queryFn: () => repo.accessMatrix(),
    staleTime: 30_000,
  });
}

/**
 * Every write below invalidates the activity log as well as its own resource,
 * because the server writes an audit row in the same transaction. Section 7.10
 * item 10. Without this the log is stale the moment an administrator does
 * anything, on the one screen where that is most obvious.
 */
function useAdminMutation<Args, Result>(mutationFn: (args: Args) => Promise<Result>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useCreateUser() {
  const repo = useAdminRepository();
  return useAdminMutation((user: NewUser) => repo.createUser(user));
}

export function useUpdateUser() {
  const repo = useAdminRepository();
  return useAdminMutation(({ id, edit }: { id: string; edit: UserEdit }) =>
    repo.updateUser(id, edit),
  );
}

export function useRevokeUser() {
  const repo = useAdminRepository();
  return useAdminMutation((id: string) => repo.revokeUser(id));
}

export function useRestoreUser() {
  const repo = useAdminRepository();
  return useAdminMutation((id: string) => repo.restoreUser(id));
}

/**
 * Saving the matrix invalidates the session too. The menu, the mobile tabs and
 * the route guard all read `session.views`, so a grant that does not reach the
 * session is a grant the user cannot see until they sign in again. Section 5.1,
 * and the acceptance criterion in section 14: the toggle changes the menu on
 * the next render.
 */
export function useSaveAccessMatrix() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (matrix: AccessMatrix) => repo.saveAccessMatrix(matrix),
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.access, saved);
      void queryClient.invalidateQueries({ queryKey: keys.session });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** Sets another person's password. The server signs them out everywhere. */
export function useSetUserPassword() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; newPassword: string }) =>
      repo.setUserPassword(input.id, input.newPassword),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** Adds an item to the price list. Intake offers it at once. */
export function useCreateCatalogItem() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (item: NewCatalogItem) => repo.createCatalogItem(item),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.catalog });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * Renames, reprices, retires or puts back an item. A rename reaches every case
 * carrying the item, so the order lists, the kits, the sheets, the archive and
 * the reports all refetch.
 */
export function useUpdateCatalogItem() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; rowVersion: string; edit: CatalogItemEdit }) =>
      repo.updateCatalogItem(input.id, input.rowVersion, input.edit),
    onSettled: () => {
      for (const queryKey of [
        keys.catalog,
        keys.orders.all,
        keys.kits.all,
        keys.printAll,
        ['archive'],
        ['reports'],
        ['activity'],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * A person's profile: your own, or anybody's with `admin`. Request of 4 October
 * 2026 (second), section 7.
 */
export function useProfile(userId: string | undefined, range: ProfileRange) {
  const repo = useAdminRepository();
  return useQuery({
    queryKey: keys.profile(userId ?? '', range),
    queryFn: () => repo.profile(userId as string, range),
    enabled: userId !== undefined && userId !== '',
    refetchInterval: liveRefetchInterval,
  });
}

/** A new photograph, or none. The session carries it to the menu, so it is refreshed too. */
export function useSetAvatar() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, file }: { userId: string; file: File }) => repo.setAvatar(userId, file),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
      void queryClient.invalidateQueries({ queryKey: keys.session });
    },
  });
}

export function useRemoveAvatar() {
  const repo = useAdminRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => repo.removeAvatar(userId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.users });
      void queryClient.invalidateQueries({ queryKey: keys.session });
    },
  });
}

/** The installed app's colours and icon set. Management only. Request of 5 October 2026. */
export function usePwaSettings(enabled = true) {
  const repo = useAdminRepository();
  return useQuery({ queryKey: keys.pwaSettings, queryFn: () => repo.pwaSettings(), enabled });
}

function usePwaMutation<Args>(mutationFn: (args: Args) => Promise<PwaSettings>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (saved) => {
      queryClient.setQueryData(keys.pwaSettings, saved);
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

export function useSavePwaSettings() {
  const repo = useAdminRepository();
  return usePwaMutation((colors: { themeColor: string; backgroundColor: string }) => repo.savePwaSettings(colors));
}

export function useUploadAppIcons() {
  const repo = useAdminRepository();
  return usePwaMutation((icons: AppIconUpload) => repo.uploadAppIcons(icons));
}

export function useResetAppIcons() {
  const repo = useAdminRepository();
  return usePwaMutation<void>(() => repo.resetAppIcons());
}
